/**
 * AuthContext - Phase 3H
 *
 * Authentication context provider with real username/password login.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '../api';
// =============================================================================
// Types
// =============================================================================

export interface User {
  id: number;
  user_id: string;
  display_name?: string;
  email?: string;
  is_admin: boolean;
  created_at: string;
  last_login_at?: string;
}

export interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  error: string | null;
  login: (userId: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

// =============================================================================
// Constants
// =============================================================================

const STORAGE_KEY = 'current_user_id';


// =============================================================================
// Context
// =============================================================================

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// =============================================================================
// Provider
// =============================================================================

export interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch user data from /users/me endpoint
   */
  const fetchUser = useCallback(async (userId: string): Promise<User | null> => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api/v1'}/users/me`, {
        headers: {
          'X-User-ID': userId,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          return null;
        }
        throw new Error(`Failed to fetch user: ${response.statusText}`);
      }

      const userData: User = await response.json();
      return userData;
    } catch (err) {
      console.error('Error fetching user:', err);
      return null;
    }
  }, []);

  /**
   * Initialize auth state from localStorage
   */
  useEffect(() => {
    const initAuth = async () => {
      setIsLoading(true);
      const storedUserId = localStorage.getItem(STORAGE_KEY);

      if (storedUserId) {
        const userData = await fetchUser(storedUserId);
        if (userData) {
          setUser(userData);
        } else {
          // Invalid stored user, clear it
          localStorage.removeItem(STORAGE_KEY);
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, [fetchUser]);

  /**
   * Login with username and password
   */
  const login = useCallback(async (userId: string, password: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      // Call login endpoint
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api/v1'}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Invalid username or password');
      }

      const loginData = await response.json();

      if (loginData.success) {
        // Store user_id and fetch full user data
        localStorage.setItem(STORAGE_KEY, loginData.user_id);
        api.setUserId(loginData.user_id);
        const userData = await fetchUser(loginData.user_id);
        if (userData) {
          setUser(userData);
        } else {
          throw new Error('Failed to fetch user data');
        }
      } else {
        throw new Error('Login failed');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchUser]);

  /**
   * Logout
   */
  const logout = useCallback((): void => {
  api.clearUserId();  // Clear ApiClient in-memory cache
  localStorage.removeItem(STORAGE_KEY);
  setUser(null);
  setError(null);
}, []);

  /**
   * Refresh user data
   */
  const refreshUser = useCallback(async (): Promise<void> => {
    const storedUserId = localStorage.getItem(STORAGE_KEY);

    if (storedUserId) {
      const userData = await fetchUser(storedUserId);
      setUser(userData);
    }
  }, [fetchUser]);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin ?? false,
    isLoading,
    error,
    login,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

// =============================================================================
// Utilities
// =============================================================================

/**
 * Get current user ID from storage (for use outside React)
 */
export function getCurrentUserId(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

export default AuthContext;